import type { Smeta, SmetaCategory } from '@holzcom/smeta-kp';
import { apiFetch, criticalApiJson } from './auth-fetch';

export type ViewerSmetaCapabilities = {
  schema: 'VIEWER_SMETA_CAPABILITIES.v1'; configured: boolean; ready: boolean; owner: 'viewer';
  sourceReleaseSha: string;
  views: Record<'smeta' | 'analysis' | 'detailed' | 'snapshots' | 'revisions' | 'comments' | 'briefs' | 'proposals', 'CLOSED' | 'ADVISORY_ONLY'>;
  actions: Record<'operationRows' | 'reprice' | 'import' | 'matching' | 'substitution' | 'aiAdvisory' | 'aiApply' | 'pricebookAdmin',
    { state: 'OPEN' | 'CLOSED'; reason: string | null }>;
};

function exactViewerSmetaCapabilities(value: ViewerSmetaCapabilities): ViewerSmetaCapabilities {
  const viewKeys = ['smeta', 'analysis', 'detailed', 'snapshots', 'revisions', 'comments', 'briefs', 'proposals'] as const;
  const actionKeys = ['operationRows', 'reprice', 'import', 'matching', 'substitution', 'aiAdvisory', 'aiApply', 'pricebookAdmin'] as const;
  if (value?.schema !== 'VIEWER_SMETA_CAPABILITIES.v1' || typeof value.configured !== 'boolean'
    || typeof value.ready !== 'boolean' || value.owner !== 'viewer'
    || typeof value.sourceReleaseSha !== 'string' || !/^[0-9a-f]{64}$/.test(value.sourceReleaseSha)
    || !value.views || viewKeys.some((key) => !['CLOSED', 'ADVISORY_ONLY'].includes(value.views[key]))
    || !value.actions || actionKeys.some((key) => {
      const action = value.actions[key];
      return !action || !['OPEN', 'CLOSED'].includes(action.state)
        || !(action.reason === null || typeof action.reason === 'string');
    })) {
    throw new Error('VIEWER_SMETA_CAPABILITIES_RESPONSE_INVALID');
  }
  return value;
}

export type CanonicalImportPreview = { schema: 'VIEWER_SMETA_CANONICAL_IMPORT_PREVIEW.v1';
  smetaId: string; modelId: string; status: string; rowVersion: number; empty: boolean;
  sourceRevision: string; sourceArtifactSha256: string; physicalProjectionRevision: string; bomSha256: string; cutSha256: string;
  canImport: boolean; counts: { sourceRows: number; projectionCatalogMapped: number; mapped: number;
    priced: number; unresolved: number; material: number; hardware: number; equipment: number; operation?: number } };

export async function previewCanonicalSmetaImport(modelId: string, smetaId: string,
    expectedRowVersion: number): Promise<CanonicalImportPreview> {
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/canonical-import/preview`, {
    method:'POST',headers:{'Content-Type':'application/json'},
    body:JSON.stringify({modelId,expectedRowVersion}),
  });
}

export async function confirmCanonicalSmetaImport(modelId: string, smetaId: string,
    expectedRowVersion: number): Promise<{ sourceRows:number; mapped:number; priced:number;
      unresolved:number; replayed:boolean; rowVersion:number; estimateVersion:number;
      estimateContentSha256:string; pricebookSha256:string }> {
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/canonical-import/confirm`, {
    method:'POST',headers:{'Content-Type':'application/json'},
    body:JSON.stringify({modelId,expectedRowVersion}),
  });
}

export type CoreDocument = { smeta_id: string; model_id: string; order_id: string | null; label: string;
  status: string; row_version: number; material_release_sha: string; total_cost: string | null; purchase_total_cost: string | null;
  price_status: 'COMPLETE' | 'NOT_FINAL'; unresolved_row_ids: string[]; demand_refusals: string[];
  versionPins?: { materialsVersionId:string|null; ratesVersionId:string|null; pricebookId:string|null;
    estimateVersion?:number; estimateContentSha256?:string; pricebookSha256?:string;
    catalogRevisionPins?:{schema:'VIEWER_SMETA_CATALOG_REVISION_PINS.v1';materials:string[];operations:string[]} } };
export type ViewerSmetaCoreRow = { id: string; row_type: string; name: string; qty: string; unit: string;
  materialOperationalId: number | null; materialCanonicalId: string | null; materialReleaseSha: string | null;
  unit_cost: string | null; cost_total: string | null; purchase_cost_total: string | null; priceStatus: 'APPROVED' | 'UNRESOLVED';
  priceAuthority: null | { currency?: string; material_id?: string; materials_version_id?: string; pricebook_id?: string; price_row_sha256?: string;
    decimal?: string; uom?: string; release_sha256?: string; schema?: string; source?: string; sourcePriceStatus?: string;
    purchaseDecimal?: string; clientDecimal?: string; pricebook_sha256?:string; catalog_revision?:string;
    override?:{schema:'VIEWER_SMETA_VERSION_PRICE_OVERRIDE.v1';revision:number;actor:string;reason:string;auditSha256:string} };
  candidateEvidence?: unknown; historicalPriceEvidence?:unknown; meta: { article?: string; materialRowSha256?: string; sourceId?: string;
    sourceReleaseSha?: string; sourceKind?: 'material' | 'hardware' | 'equipment' | 'operation' | string;
    schema?:string; sourceEntityId?:string; sourceRowId?:string; rowId?:string; catalogId?:string; catalogRevisionId?:string|null;
    sourceUnit?:string; authorityCardUnit?:string|null; uomMatch?:boolean; cutRequired?:boolean;
    bomSha256?:string; cutSha256?:string; sourceRevision?:string; sourceArtifactSha256?:string;
    physicalProjectionRevision?:string;
    sourceEvidence?: { article?: string; viewerDefaultPrice?: { authority?: string; identity?: {
      family?: string; operationType?: string; rateSourceId?: string; article?: string;
      operationSourceId?: string } } } } };

export type HumanPriceRow = { materialId:number; purchaseDecimal:string; clientDecimal:string; uom:string;
  reason:string; sourceReference:string };

export type NativeQuoteVersion={quoteVersion:number;estimateVersion:number;label:string;
  totalCost:string|null;purchaseTotalCost:string|null;price_status:'COMPLETE'|'NOT_FINAL';unresolved_row_ids:string[];contentSha256:string;createdAt?:string;rowsCount?:number};
export type NativeEstimateRows={rows:ViewerSmetaCoreRow[];row_version:number;total_cost:string|null;purchase_total_cost:string|null;
  price_status:'COMPLETE'|'NOT_FINAL';unresolved_row_ids:string[];demand_refusals:string[];
  versionPins:NonNullable<CoreDocument['versionPins']>};
export const nativeQuoteApi={
  rows:loadNativeEstimateRows,
  versions:loadNativeQuoteVersions,
  save:saveNativeQuoteVersion,
  price:(smetaId:string,estimateVersion:number,rowId:string,value:{purchaseDecimal:string;clientDecimal:string;
    uom:string;reason:string;expectedRevision:number}):Promise<unknown>=>json(
    `/api/arm/smeta/${encodeURIComponent(smetaId)}/estimates/${estimateVersion}/rows/${encodeURIComponent(rowId)}/price-override`,{
      method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(value)}),
  link:(smetaId:string,quoteVersion:number):Promise<{url:string;exportId:string}>=>json(
    `/api/arm/smeta/${encodeURIComponent(smetaId)}/quote-versions/${quoteVersion}/client-link`,{method:'POST'}),
};
export type HumanPricebookDraft = { schema:'VIEWER_HUMAN_PRICEBOOK_DRAFT.v1'; pricebookId:string;
  status:'DRAFT'|'APPROVED'; version:number; contentSha256:string; createdBy:string; rows:Array<HumanPriceRow&{
    kind:string; article:string; releaseSha256:string; materialRowSha256:string; rowSha256:string }> };
export type HumanPricebookApproval = { pricebookId:string; status:'APPROVED'; pointerRevision:number;
  contentSha256:string; replayed:boolean };
export type HumanPricebookCreated = { pricebookId:string; status:'DRAFT'|'APPROVED'|'RETIRED'; contentSha256:string;
  releaseSha256:string; replayed:boolean; createdBy:string };
export type HumanPricebookCsvCreated=HumanPricebookCreated&{previewSha256:string;uploadSha256:string;
  normalizedRowsSha256:string};
export type HumanPricebookCsvPreview={schema:'VIEWER_HUMAN_PRICEBOOK_CSV_PREVIEW.v1';uploadSha256:string;
  releaseSha256:string;columns:string[];acceptedRows:number;rejectedRows:number;duplicateRows:number;
  unresolvedRows:number;normalizedRowsSha256:string;errorReportSha256:string;previewSha256:string;
  draftContentSha256:string;
  diagnostics:Array<{line:number;code:string;field:string}>;diagnosticsTruncated:boolean;canCreateDraft:boolean};

export async function createHumanPricebookDraft(releaseSha256:string,rows:HumanPriceRow[]):Promise<HumanPricebookCreated> {
  return json('/api/arm/smeta/pricebooks/drafts',{method:'POST',headers:{'Content-Type':'application/json'},
    body:JSON.stringify({releaseSha256,rows})});
}

export async function readHumanPricebook(pricebookId:string):Promise<HumanPricebookDraft> {
  return json(`/api/arm/smeta/pricebooks/${encodeURIComponent(pricebookId)}`,{headers:{Accept:'application/json'}});
}

export async function approveHumanPricebook(pricebookId:string,expectedContentSha256:string,
    expectedPointerRevision:number):Promise<HumanPricebookApproval> {
  return json(`/api/arm/smeta/pricebooks/${encodeURIComponent(pricebookId)}/approve`,{
    method:'POST',headers:{'Content-Type':'application/json'},
    body:JSON.stringify({expectedContentSha256,expectedPointerRevision})});
}

async function pricebookCsv<T>(path:string,file:File,fields:Record<string,string>):Promise<T>{
  const body=new FormData();body.append('file',file);Object.entries(fields).forEach(([key,value])=>body.append(key,value));
  const response=await apiFetch(path,{method:'POST',body});
  if(!response.ok){let detail=`VIEWER_PRICEBOOK_CSV_${response.status}`;try{const payload=await response.json() as {detail?:unknown};
      if(typeof payload.detail==='string')detail=payload.detail}catch{/* preserve named HTTP failure */}
    const error=new Error(detail) as Error&{status?:number};
    error.status=response.status;throw error}return response.json() as Promise<T>;
}
export function previewHumanPricebookCsv(file:File,releaseSha256:string):Promise<HumanPricebookCsvPreview>{
  return pricebookCsv('/api/arm/smeta/pricebooks/csv/preview',file,{releaseSha256});
}
export function createHumanPricebookCsv(file:File,releaseSha256:string,expectedPreviewSha256:string):Promise<HumanPricebookCsvCreated>{
  return pricebookCsv('/api/arm/smeta/pricebooks/csv/draft',file,{releaseSha256,expectedPreviewSha256});
}

async function smetaFetch(path: string, init?: RequestInit): Promise<Response> {
  try {
    return await apiFetch(path, { ...init, signal: undefined });
  } catch (cause) {
    throw Object.assign(new Error('VIEWER_SMETA_COMMAND_OUTCOME_UNKNOWN'), { cause });
  }
}

class ViewerSmetaHttpError extends Error {
  constructor(readonly status:number,readonly payload:unknown) {
    super(`VIEWER_SMETA_${status}`);
  }
}

// Fresh database/security readiness checks can exceed the ordinary read budget.
// Keep this exception on the preflight only, without caching its authority.
export const SMETA_CAPABILITIES_TIMEOUT_MS = 30_000;

async function json<T>(path: string, init?: RequestInit, timeoutMs?: number): Promise<T> {
  const read = (init?.method ?? 'GET').toUpperCase() === 'GET';
  const decode=async(response:Response):Promise<T>=>{
    if (!response.ok) {
      const payload=await response.json().catch(()=>null) as unknown;
      throw new ViewerSmetaHttpError(response.status,payload);
    }
    return await response.json() as T;
  };
  if(read)return criticalApiJson(path,init,decode,timeoutMs);
  return decode(await smetaFetch(path,init));
}

export async function loadViewerSmetaCapabilities(): Promise<ViewerSmetaCapabilities> {
  return exactViewerSmetaCapabilities(await json('/api/arm/smeta/capabilities', {
    headers: { Accept: 'application/json' },
  }, SMETA_CAPABILITIES_TIMEOUT_MS));
}

export async function resolveOrCreateViewerSmeta(modelId: string): Promise<{ smeta: Smeta; document: CoreDocument; rows: ViewerSmetaCoreRow[] }> {
  const resolved = await json<{ smeta: CoreDocument; created: boolean }>('/api/arm/smeta/resolve-or-create', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model_id: modelId, label: `Смета · ${modelId}` }),
  });
  const document=exactModelDocument(resolved.smeta,modelId);
  return loadResolvedDocument(document);
}

type ResolvedViewerSmeta = { smeta: Smeta; document: CoreDocument; rows: ViewerSmetaCoreRow[] };
type ResolutionEntry = { state:'inflight'|'ambiguous'; promise:Promise<unknown>|null };

export class BoundedSmetaResolutionCoordinator {
  private readonly entries=new Map<string,ResolutionEntry>();
  constructor(private readonly maximumEntries=64) {
    if(!Number.isInteger(maximumEntries)||maximumEntries<1)
      throw new Error('VIEWER_SMETA_COORDINATOR_POLICY_INVALID');
  }
  get size():number { return this.entries.size; }
  resetSession():void { this.entries.clear(); }
  reconciled(scope:string):void {
    if(this.entries.get(scope)?.state==='ambiguous')this.entries.delete(scope);
  }
  async readFirst<T>(scope:string,read:()=>Promise<T|null>,factory:()=>Promise<T>):Promise<T> {
    const entry=this.entries.get(scope);
    if(entry?.state==='inflight')return entry.promise as Promise<T>;
    if(entry?.state==='ambiguous') {
      const reconciled=await read();
      if(reconciled!==null) { this.reconciled(scope);return reconciled; }
      throw new Error('VIEWER_SMETA_COMMAND_OUTCOME_UNKNOWN');
    }
    if(this.entries.size>=this.maximumEntries) {
      const existing=await read();
      if(existing!==null)return existing;
      throw new Error('VIEWER_SMETA_COORDINATOR_CAPACITY');
    }
    return this.run(scope,async()=>{
      const existing=await read();
      return existing!==null?existing:factory();
    });
  }
  run<T>(scope:string,factory:()=>Promise<T>):Promise<T> {
    const existing=this.entries.get(scope);
    if (existing?.state==='inflight') return existing.promise as Promise<T>;
    if (existing?.state==='ambiguous') return Promise.reject(new Error('VIEWER_SMETA_COMMAND_OUTCOME_UNKNOWN'));
    if(this.entries.size>=this.maximumEntries) {
      // An ambiguous command may still be running after the caller's deadline.
      // Capacity can only be released by an authoritative read or auth-session
      // revocation, never by LRU eviction that could authorize a second POST.
      return Promise.reject(new Error('VIEWER_SMETA_COORDINATOR_CAPACITY'));
    }
    const promise=factory();
    const entry:ResolutionEntry={state:'inflight',promise};
    this.entries.set(scope,entry);
    void promise.then(()=>{
      if(this.entries.get(scope)===entry)this.entries.delete(scope);
    },(error:unknown)=>{
      if(this.entries.get(scope)!==entry)return;
      if(error instanceof Error&&error.message==='VIEWER_SMETA_COMMAND_OUTCOME_UNKNOWN') {
        // The command is deliberately not aborted.  Until a durable read proves its
        // result, forgetting this tombstone would permit a second command while the
        // first request can still commit on the server.
        entry.state='ambiguous';entry.promise=null;
      } else this.entries.delete(scope);
    });
    return promise;
  }
}

const resolutionCoordinator=new BoundedSmetaResolutionCoordinator();

/** Auth/cache lifecycle owner calls this before a principal scope is replaced. */
export function resetViewerSmetaResolutionSession():void {
  resolutionCoordinator.resetSession();
}

const DECIMAL=/^(?:0|[1-9]\d*)(?:\.\d+)?$(?![\s\S])/;
const UUID=/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HEX64=/^[0-9a-f]{64}$(?![\s\S])/;

function compareSourceIds(left:string,right:string):number {
  const bytes=new TextEncoder();const a=bytes.encode(left),b=bytes.encode(right);
  for(let i=0;i<Math.min(a.length,b.length);i++)if(a[i]!==b[i])return a[i]-b[i];
  return a.length-b.length;
}

/** Validate backend readiness and declared unknown rows; never derive a final money total here. */
function exactPriceReadiness(value:Record<string,unknown>,rows?:readonly ViewerSmetaCoreRow[]):void {
  const ids=value.unresolved_row_ids;
  const refusals=value.demand_refusals;
  if(!['COMPLETE','NOT_FINAL'].includes(String(value.price_status))||!Array.isArray(ids)||
      !Array.isArray(refusals)||refusals.some(reason=>typeof reason!=='string'||!reason||reason!==reason.trim())||
      new Set(refusals).size!==refusals.length||
      ids.some(id=>typeof id!=='string'||!id)||new Set(ids).size!==ids.length||
      ids.some((id,index)=>index>0&&compareSourceIds(ids[index-1] as string,id as string)>=0)||
      (value.price_status==='NOT_FINAL'?value.total_cost!==null||value.purchase_total_cost!==null:
        ids.length!==0||refusals.length!==0||['total_cost','purchase_total_cost'].some(key=>typeof value[key]!=='string'||!DECIMAL.test(value[key] as string))))
    throw new Error('VIEWER_SMETA_PRICE_READINESS_INVALID');
  if(rows) {
    // A backend physical-demand refusal makes the entire row cohort NOT_FINAL
    // even when every known row carries an approved monetary rate.
    const unknown=(refusals.length?rows:rows.filter(row=>row.priceStatus==='UNRESOLVED')).map(row=>row.id).sort(compareSourceIds);
    if(new Set(rows.map(row=>row.id)).size!==rows.length||JSON.stringify(ids)!==JSON.stringify(unknown)||
        value.price_status==='COMPLETE'&&(!rows.length||unknown.length))
      throw new Error('VIEWER_SMETA_PRICE_COVERAGE_INVALID');
  }
}

function exactPins(value:unknown):CoreDocument['versionPins'] {
  if(!value||typeof value!=='object'||Array.isArray(value))throw new Error('VIEWER_SMETA_RESOLUTION_INVALID');
  const pins=value as Record<string,unknown>;
  const base=['materialsVersionId','ratesVersionId','pricebookId'];
  const estimate=['estimateVersion','estimateContentSha256','pricebookSha256'];
  const keys=Object.keys(pins).sort();
  const expected=(keys.length===base.length?base:[...base,...estimate,
    ...('catalogRevisionPins' in pins?['catalogRevisionPins']:[])]).sort();
  if(keys.length!==expected.length||keys.some((key,index)=>key!==expected[index]))
    throw new Error('VIEWER_SMETA_RESOLUTION_INVALID');
  if('catalogRevisionPins' in pins){
    const revisions=pins.catalogRevisionPins as Record<string,unknown>|null;
    if(!revisions||typeof revisions!=='object'||Array.isArray(revisions)||
      Object.keys(revisions).sort().join(',')!=='materials,operations,schema'||
      revisions.schema!=='VIEWER_SMETA_CATALOG_REVISION_PINS.v1')throw new Error('VIEWER_SMETA_RESOLUTION_INVALID');
    for(const key of ['materials','operations']){
      const values=revisions[key];
      if(!Array.isArray(values)||values.some(v=>typeof v!=='string'||!v.trim())||new Set(values).size!==values.length)
        throw new Error('VIEWER_SMETA_RESOLUTION_INVALID');
      // UTF-8 byte order matches PostgreSQL C and Python Unicode code points,
      // including supplementary-plane source labels (not JS UTF-16 sorting).
      const bytes=new TextEncoder();
      const compare=(a:string,b:string)=>{const aa=bytes.encode(a),bb=bytes.encode(b);
        for(let i=0;i<Math.min(aa.length,bb.length);i++)if(aa[i]!==bb[i])return aa[i]-bb[i];
        return aa.length-bb.length;};
      if(values.some((v,i)=>i>0&&compare(values[i-1],v)>=0))throw new Error('VIEWER_SMETA_RESOLUTION_INVALID');
    }
  }
  for(const key of base)if(pins[key]!==null&&(typeof pins[key]!=='string'||!UUID.test(pins[key] as string)))
    throw new Error('VIEWER_SMETA_RESOLUTION_INVALID');
  if(keys.length>base.length&&(!Number.isInteger(pins.estimateVersion)||Number(pins.estimateVersion)<1||
      typeof pins.estimateContentSha256!=='string'||!HEX64.test(pins.estimateContentSha256)||
      typeof pins.pricebookSha256!=='string'||!HEX64.test(pins.pricebookSha256)))
    throw new Error('VIEWER_SMETA_RESOLUTION_INVALID');
  // PostgreSQL JSONB reorders object keys. Identity is the validated values,
  // not insertion order; revision arrays remain ordered and checked above.
  return Object.fromEntries(expected.map(key => [key, key === 'catalogRevisionPins'
    ? { schema: pins.catalogRevisionPins && (pins.catalogRevisionPins as Record<string, unknown>).schema,
        materials: (pins.catalogRevisionPins as Record<string, unknown>).materials,
        operations: (pins.catalogRevisionPins as Record<string, unknown>).operations }
    : pins[key]])) as CoreDocument['versionPins'];
}

function exactDocument(value:unknown,expected:{modelId?:string;orderId?:string|null}):CoreDocument {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('VIEWER_SMETA_RESOLUTION_INVALID');
  const row=value as Record<string,unknown>;
  const required=['smeta_id','model_id','order_id','label','status','row_version',
    'material_release_sha','total_cost','purchase_total_cost','price_status','unresolved_row_ids','demand_refusals','versionPins'];
  if(Object.keys(row).length!==required.length||Object.keys(row).some((key)=>!required.includes(key)))
    throw new Error('VIEWER_SMETA_RESOLUTION_INVALID');
  if (typeof row.smeta_id !== 'string' || !row.smeta_id || typeof row.model_id!=='string'||!row.model_id ||
      (expected.modelId!==undefined&&row.model_id!==expected.modelId)||(expected.orderId!==undefined&&row.order_id!==expected.orderId)||
      !(row.order_id===null||typeof row.order_id==='string'&&row.order_id.length>0)||
      typeof row.label !== 'string' || !row.label || !['DRAFT','VALIDATED','FROZEN'].includes(String(row.status)) ||
      !Number.isInteger(row.row_version) || Number(row.row_version)<1 ||
      typeof row.material_release_sha !== 'string' || !HEX64.test(row.material_release_sha) ||
      (row.total_cost!==null&&(typeof row.total_cost !== 'string'||!DECIMAL.test(row.total_cost)))) throw new Error('VIEWER_SMETA_RESOLUTION_INVALID');
  exactPriceReadiness(row);
  return {...row,versionPins:exactPins(row.versionPins)} as unknown as CoreDocument;
}

const exactModelDocument=(value:unknown,modelId:string)=>exactDocument(value,{modelId,orderId:null});

function isExactMissing(error:unknown):boolean {
  if(!(error instanceof ViewerSmetaHttpError)||error.status!==404||!error.payload||
      typeof error.payload!=='object'||Array.isArray(error.payload))return false;
  const payload=error.payload as Record<string,unknown>;
  if(Object.keys(payload).sort().join(',')!=='code,detail,message,status'||payload.status!==404||
      payload.code!=='RESOURCE_NOT_FOUND'||payload.message!=='SMETA_NOT_FOUND'||
      !payload.detail||typeof payload.detail!=='object'||Array.isArray(payload.detail))return false;
  const detail=payload.detail as Record<string,unknown>;
  return Object.keys(detail).sort().join(',')==='code,message'&&
    detail.code==='RESOURCE_NOT_FOUND'&&detail.message==='SMETA_NOT_FOUND';
}

async function readResolvedModel(modelId:string):Promise<ResolvedViewerSmeta|null> {
  try {
    const raw=await json<unknown>(`/api/arm/smeta/resolve?model_id=${encodeURIComponent(modelId)}`,
      {headers:{Accept:'application/json'}});
    return loadResolvedDocument(exactModelDocument(raw,modelId));
  } catch (error) {
    if (isExactMissing(error)) return null;
    throw error;
  }
}

const wait=(milliseconds:number)=>new Promise<void>((resolve)=>setTimeout(resolve,milliseconds));

async function commandThenReconcile<T>(command:Promise<T>,read:()=>Promise<T|null>,policy:{commandDeadlineMs?:number;
    reconcileAttempts?:number;reconcileDelayMs?:number},onAmbiguous?:()=>void):Promise<T> {
  const deadline=policy.commandDeadlineMs ?? 4000;
  const attempts=policy.reconcileAttempts ?? 6;
  const delay=policy.reconcileDelayMs ?? 500;
  if(!Number.isFinite(deadline)||deadline<0||!Number.isInteger(attempts)||attempts<1||
      !Number.isFinite(delay)||delay<0)throw new Error('VIEWER_SMETA_RESOLUTION_POLICY_INVALID');
  let timer:ReturnType<typeof setTimeout>|undefined;
  const deadlineResult=new Promise<{kind:'unknown'}>((resolve)=>{timer=setTimeout(()=>resolve({kind:'unknown'}),deadline)});
  try {
    const first=await Promise.race([
      command.then((value)=>({kind:'result' as const,value}),(error:unknown)=>
        error instanceof ViewerSmetaHttpError
          ? ({kind:'failure' as const,error}) : ({kind:'unknown' as const})),
      deadlineResult,
    ]);
    if (first.kind==='result') return first.value;
    if (first.kind==='failure') throw first.error;
  } finally { if(timer!==undefined)clearTimeout(timer); }
  for (let index=0;index<attempts;index+=1) {
    const reconciled=await read();
    if (reconciled) return reconciled;
    if (index+1<attempts) await wait(delay);
  }
  onAmbiguous?.();
  throw new Error('VIEWER_SMETA_COMMAND_OUTCOME_UNKNOWN');
}

/** Read first, issue at most one unabortable command, then reconcile its durable result. */
export async function resolveViewerSmetaBounded(modelId:string, policy:{commandDeadlineMs?:number;
    reconcileAttempts?:number;reconcileDelayMs?:number}={}):Promise<ResolvedViewerSmeta> {
  const scope=`model:${modelId}`;
  return resolutionCoordinator.readFirst(scope,()=>readResolvedModel(modelId),()=>{
    const command=resolveOrCreateViewerSmeta(modelId);
    return commandThenReconcile(command,()=>readResolvedModel(modelId),policy,()=>{
      void command.then(async()=>{
        if(await readResolvedModel(modelId))resolutionCoordinator.reconciled(scope);
      }).catch(()=>undefined);
    });
  });
}

export type ViewerOrderSmetaContext = {
  schema: 'VIEWER_ORDER_SMETA_CONTEXT.v1'; created: boolean;
  order: { orderId: string; title: string; modelId: string; revisionId:string; revisionIndex:number };
  document: CoreDocument; rows: ViewerSmetaCoreRow[]; smeta: Smeta;
};

type OrderHint={modelId?:string;revisionId?:string;revisionIndex?:number};
type OrderPolicy={commandDeadlineMs?:number;reconcileAttempts?:number;reconcileDelayMs?:number};

function exactOrderHint(orderId:string,hint:OrderHint):OrderHint {
  if(!orderId||!hint||(hint.modelId!==undefined&&(!hint.modelId||typeof hint.modelId!=='string'))||
      (hint.revisionId!==undefined&&(!hint.revisionId||typeof hint.revisionId!=='string'))||
      (hint.revisionIndex!==undefined&&(!Number.isInteger(hint.revisionIndex)||hint.revisionIndex<1))||
      ((hint.revisionId===undefined)!==(hint.revisionIndex===undefined)))
    throw new Error('VIEWER_ORDER_SMETA_BINDING_MISMATCH');
  return {...hint};
}

function orderResolutionPath(orderId:string,action:'context'|'resolve',hint:OrderHint):string {
  const params=new URLSearchParams();
  if(hint.modelId!==undefined)params.set('expected_model_id',hint.modelId);
  if(hint.revisionId!==undefined)params.set('expected_revision_id',hint.revisionId);
  if(hint.revisionIndex!==undefined)params.set('expected_revision_index',String(hint.revisionIndex));
  const query=params.toString();
  const path=`/api/arm/smeta/orders/${encodeURIComponent(orderId)}/${action}`;
  return query?`${path}?${query}`:path;
}

async function readResolvedOrder(orderId:string,hint:OrderHint):Promise<ViewerOrderSmetaContext|null> {
  try {
    const raw=await json<unknown>(orderResolutionPath(orderId,'context',hint),
      {headers:{Accept:'application/json'}});
    const resolved=await exactOrderResolution(raw,orderId,hint);
    return resolved;
  } catch(error) {
    if(isExactMissing(error))return null;
    throw error;
  }
}

async function commandOrderResolution(orderId:string,hint:OrderHint):Promise<ViewerOrderSmetaContext> {
  const raw=await json<unknown>(orderResolutionPath(orderId,'resolve',hint),{method:'POST'});
  return exactOrderResolution(raw,orderId,hint);
}

async function exactOrderResolution(raw:unknown,orderId:string,hint:OrderHint):Promise<ViewerOrderSmetaContext> {
  if(!raw||typeof raw!=='object'||Array.isArray(raw))throw new Error('VIEWER_ORDER_SMETA_BINDING_MISMATCH');
  const resolved=raw as Record<string,unknown>;
  if(Object.keys(resolved).length!==4||Object.keys(resolved).some((key)=>
      !['schema','created','order','smeta'].includes(key))||
      resolved.schema!=='VIEWER_ORDER_SMETA_CONTEXT.v1'||typeof resolved.created!=='boolean'||
      !resolved.order||typeof resolved.order!=='object'||Array.isArray(resolved.order))
    throw new Error('VIEWER_ORDER_SMETA_BINDING_MISMATCH');
  const order=resolved.order as Record<string,unknown>;
  const orderKeys=['orderId','title','status','revisionId','revisionIndex','modelId','modelName'];
  if(Object.keys(order).length!==orderKeys.length||Object.keys(order).some((key)=>!orderKeys.includes(key))||
      order.orderId!==orderId||typeof order.title!=='string'||!order.title||typeof order.status!=='string'||
      typeof order.revisionId!=='string'||!order.revisionId||!Number.isInteger(order.revisionIndex)||
      Number(order.revisionIndex)<1||
      typeof order.modelId!=='string'||!order.modelId||typeof order.modelName!=='string'||!order.modelName||
      (hint.modelId!==undefined&&order.modelId!==hint.modelId)||
      (hint.revisionId!==undefined&&order.revisionId!==hint.revisionId)||
      (hint.revisionIndex!==undefined&&order.revisionIndex!==hint.revisionIndex))
    throw new Error('VIEWER_ORDER_SMETA_BINDING_MISMATCH');
  let document:CoreDocument;
  try { document=exactDocument(resolved.smeta,{modelId:String(order.modelId),orderId}); }
  catch { throw new Error('VIEWER_ORDER_SMETA_BINDING_MISMATCH'); }
  const loaded=await loadResolvedDocument(document);
  return {schema:'VIEWER_ORDER_SMETA_CONTEXT.v1',created:resolved.created,
    order:{orderId,title:String(order.title),modelId:String(order.modelId),
      revisionId:String(order.revisionId),revisionIndex:Number(order.revisionIndex)},...loaded};
}

export async function resolveOrderViewerSmeta(orderId:string,hint:OrderHint,
    policy:OrderPolicy={}):Promise<ViewerOrderSmetaContext> {
  const expected=exactOrderHint(orderId,hint);
  const scope=`order:${orderId}:model:${expected.modelId ?? '<server-bound>'}:revision:${expected.revisionId ?? '<server-bound>'}:${expected.revisionIndex ?? 0}`;
  return resolutionCoordinator.readFirst(scope,()=>readResolvedOrder(orderId,expected),()=>{
    const command=commandOrderResolution(orderId,expected);
    return commandThenReconcile(command,()=>readResolvedOrder(orderId,expected),policy,()=>{
      // A response arriving after the bounded caller has reported ambiguity is
      // verified by a document read before its exact scope is released.
      void command.then(async(result)=>{
        const durable=await readResolvedOrder(orderId,result.order);
        if(durable)resolutionCoordinator.reconciled(scope);
      }).catch(()=>undefined);
    });
  });
}

export type ViewerSmetaRevisionItem = { snapshotNo:number; sourceRowVersion:number; comment:string;
  totalCost:string|null;purchaseTotalCost:string|null;price_status:'COMPLETE'|'NOT_FINAL';unresolved_row_ids:string[];rowsCount:number; contentSha256:string; createdBy:string; createdAt:string };
export type ViewerSmetaRevisionRow = { id:string; rowType:string; name:string; qty:string; unit:string;
  materialOperationalId:number|null; materialCanonicalId:string|null; materialReleaseSha256:string;
  priceStatus:'APPROVED'|'UNRESOLVED'; unitCost:string|null; costTotal:string|null;purchaseCostTotal:string|null;
  priceAuthority:Record<string,unknown>|null };
export type ViewerSmetaRevisionList = { schema:'VIEWER_SMETA_REVISIONS.v1'; document:CoreDocument;
  order:ViewerOrderSmetaContext['order']|null; items:ViewerSmetaRevisionItem[] };
export type ViewerSmetaRevisionDetail = { schema:'VIEWER_SMETA_REVISION_DETAIL.v1'; document:CoreDocument;
  order:ViewerOrderSmetaContext['order']|null;
  revision:ViewerSmetaRevisionItem&{materialReleaseSha256:string;versionPins:Record<string,string|null>;rows:ViewerSmetaRevisionRow[]};
  current:{rowVersion:number;status:string;totalCost:string|null;purchaseTotalCost:string|null;price_status:'COMPLETE'|'NOT_FINAL';unresolved_row_ids:string[];rowsCount:number};
  comparison:{added:ViewerSmetaRevisionRow[];removed:ViewerSmetaRevisionRow[];
    changed:Array<{id:string;before:ViewerSmetaRevisionRow;after:ViewerSmetaRevisionRow;
      fields:Record<string,{before:unknown;after:unknown}>}>;
    totalBefore:string|null;totalAfter:string|null;deltaTotal:string|null};
  restore:{allowed:boolean;reason:string|null;ifMatch:string} };

export async function listViewerSmetaRevisions(smetaId:string):Promise<ViewerSmetaRevisionList> {
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/revisions`,{headers:{Accept:'application/json'}});
}

export async function loadViewerSmetaRevision(smetaId:string,snapshotNo:number):Promise<ViewerSmetaRevisionDetail> {
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/revisions/${snapshotNo}`,{headers:{Accept:'application/json'}});
}

export async function restoreViewerSmetaRevision(smetaId:string,snapshotNo:number,rowVersion:number):Promise<{
  schema:'VIEWER_SMETA_REVISION_RESTORE.v1';ok:true;restored_no:number;backup_no:number;
  total_cost:string|null;purchase_total_cost:string|null;price_status:'COMPLETE'|'NOT_FINAL';unresolved_row_ids:string[];row_version:number}> {
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/revisions/${snapshotNo}/restore`,{
    method:'POST',headers:{'If-Match':`"rv:${rowVersion}"`},
  });
}

// Backend owns the versioned evidence payloads.  The UI treats their contents as
// opaque governed JSON and validates only the row's exact outer envelope.
function opaqueGovernedJson(value:unknown):boolean {
  if(value===null||typeof value==='string'||typeof value==='boolean')return true;
  if(typeof value==='number')return Number.isFinite(value);
  if(Array.isArray(value))return value.every(opaqueGovernedJson);
  if(!value||typeof value!=='object')return false;
  return Object.getPrototypeOf(value)===Object.prototype&&Object.values(value as Record<string,unknown>).every(opaqueGovernedJson);
}

function decimalParts(value:string):{integer:bigint;scale:number} {
  const [whole,fraction='']=value.split('.');
  return {integer:BigInt(whole+fraction),scale:fraction.length};
}
function decimalEqualProduct(total:string,left:string,right:string):boolean {
  const a=decimalParts(left),b=decimalParts(right),t=decimalParts(total);
  const product=a.integer*b.integer;const scale=a.scale+b.scale;
  return product*10n**BigInt(Math.max(0,t.scale-scale))===t.integer*10n**BigInt(Math.max(0,scale-t.scale));
}

function exactCoreRow(value:unknown,document:CoreDocument):ViewerSmetaCoreRow {
  if(!value||typeof value!=='object'||Array.isArray(value))throw new Error('VIEWER_SMETA_ROWS_INVALID');
  const row=value as Record<string,unknown>;
  const required=['id','row_type','name','qty','unit','materialOperationalId','materialCanonicalId',
    'materialReleaseSha','unit_cost','cost_total','purchase_cost_total','priceStatus','priceAuthority','candidateEvidence','meta'];
  if('historicalPriceEvidence' in row){
    required.push('historicalPriceEvidence');
    if(row.priceStatus!=='UNRESOLVED'||!opaqueGovernedJson(row.historicalPriceEvidence))throw new Error('VIEWER_SMETA_HISTORICAL_PRICE_INVALID');
  }
  if(Object.keys(row).length!==required.length||Object.keys(row).some((key)=>!required.includes(key))||
      typeof row.id!=='string'||!row.id||typeof row.row_type!=='string'||!row.row_type||
      typeof row.name!=='string'||!row.name||typeof row.qty!=='string'||!DECIMAL.test(row.qty)||
      Number(row.qty)<0||!Number.isFinite(Number(row.qty))||typeof row.unit!=='string'||!row.unit||
      (row.materialOperationalId!==null&&(!Number.isInteger(row.materialOperationalId)||Number(row.materialOperationalId)<1))||
      (row.materialCanonicalId!==null&&(typeof row.materialCanonicalId!=='string'||!row.materialCanonicalId))||
      (row.materialReleaseSha!==null&&(typeof row.materialReleaseSha!=='string'||!HEX64.test(row.materialReleaseSha)))||
      (row.unit_cost!==null&&(typeof row.unit_cost!=='string'||!DECIMAL.test(row.unit_cost)))||
      (row.cost_total!==null&&(typeof row.cost_total!=='string'||!DECIMAL.test(row.cost_total)))||
      (row.purchase_cost_total!==null&&(typeof row.purchase_cost_total!=='string'||!DECIMAL.test(row.purchase_cost_total)))||
      !['APPROVED','UNRESOLVED'].includes(String(row.priceStatus))||!opaqueGovernedJson(row.candidateEvidence))
    throw new Error('VIEWER_SMETA_ROWS_INVALID');
  if(row.priceAuthority!==null) {
    if(!row.priceAuthority||typeof row.priceAuthority!=='object'||Array.isArray(row.priceAuthority))
      throw new Error('VIEWER_SMETA_ROWS_INVALID');
    const authority=row.priceAuthority as Record<string,unknown>;
    const allowed=['currency','material_id','materials_version_id','pricebook_id','release_sha256','price_row_sha256',
      'pricebook_sha256','catalog_revision','uom','decimal','purchaseDecimal','clientDecimal',
      'schema','source','sourcePriceStatus','override'];
    if(Object.keys(authority).some((key)=>!allowed.includes(key))||!opaqueGovernedJson(authority))
      throw new Error('VIEWER_SMETA_ROWS_INVALID');
  }
  if(row.priceStatus==='UNRESOLVED') {
    if(row.unit_cost!==null||row.cost_total!==null||row.purchase_cost_total!==null||row.priceAuthority!==null)
      throw new Error('VIEWER_SMETA_ROWS_INVALID');
  } else {
    const authority=row.priceAuthority as Record<string,unknown>|null;
    const orderPrice=authority?.schema==='VIEWER_SMETA_ORDER_PRICE_OVERRIDE.v1';
    const override=authority?.override as Record<string,unknown>|undefined;
    if(override!==undefined&&(!override||typeof override!=='object'||Array.isArray(override)||
        Object.keys(override).sort().join(',')!=='actor,auditSha256,reason,revision,schema'||
        override.schema!=='VIEWER_SMETA_VERSION_PRICE_OVERRIDE.v1'||!Number.isInteger(override.revision)||Number(override.revision)<1||
        typeof override.actor!=='string'||!override.actor.trim()||typeof override.reason!=='string'||!override.reason.trim()||
        typeof override.auditSha256!=='string'||!HEX64.test(override.auditSha256)))throw new Error('VIEWER_SMETA_OVERRIDE_INVALID');
    const required=orderPrice?['schema','source','sourcePriceStatus','currency','uom','release_sha256',
      'decimal','purchaseDecimal','clientDecimal','override']:['decimal','purchaseDecimal','clientDecimal','currency','uom','material_id','materials_version_id',
      'pricebook_id','release_sha256','price_row_sha256','pricebook_sha256','catalog_revision',...(override?['override']:[])];
    // Name the drift instead of collapsing it into the generic code.  A row
    // stored before the price-authority contract grew purchaseDecimal /
    // clientDecimal / pricebook_sha256 / catalog_revision fails the length
    // check below, and "VIEWER_SMETA_ROWS_INVALID" alone gives an operator
    // nothing to act on — the whole estimate just reads "unavailable".
    if(authority&&typeof authority==='object'&&!Array.isArray(authority)) {
      const missing=required.filter((key)=>!(key in authority));
      const unexpected=Object.keys(authority).filter((key)=>!required.includes(key));
      if(missing.length||unexpected.length)
        throw new Error(`VIEWER_SMETA_ROWS_INVALID: priceAuthority shape drift on row ${row.id}`+
          (missing.length?`; missing ${missing.join(', ')}`:'')+
          (unexpected.length?`; unexpected ${unexpected.join(', ')}`:''));
    }
    if(!authority||Object.keys(authority).length!==required.length||required.some((key)=>!(key in authority))||
        typeof row.unit_cost!=='string'||typeof row.cost_total!=='string'||typeof row.purchase_cost_total!=='string'||!DECIMAL.test(row.purchase_cost_total)||
        authority.clientDecimal!==row.unit_cost||authority.decimal!==row.unit_cost||authority.uom!==row.unit||
        typeof authority.purchaseDecimal!=='string'||!DECIMAL.test(authority.purchaseDecimal)||Number(authority.purchaseDecimal)<=0||
        authority.currency!=='RUB'||authority.release_sha256!==row.materialReleaseSha||
        authority.release_sha256!==document.material_release_sha||
        (orderPrice?(authority.source!=='manager_order_price'||authority.sourcePriceStatus!=='UNRESOLVED'||!override||
          !['material','hardware','operation'].includes(String(row.row_type))||document.versionPins?.estimateVersion===undefined):
        (typeof authority.material_id!=='string'||!UUID.test(authority.material_id)||
        authority.materials_version_id!==document.versionPins?.materialsVersionId||
        authority.pricebook_id!==document.versionPins?.pricebookId||typeof authority.price_row_sha256!=='string'||
        !HEX64.test(authority.price_row_sha256)||typeof authority.pricebook_sha256!=='string'||!HEX64.test(authority.pricebook_sha256)||
        (document.versionPins?.pricebookSha256!==undefined&&authority.pricebook_sha256!==document.versionPins.pricebookSha256)||
        typeof authority.catalog_revision!=='string'||!authority.catalog_revision))||
        !decimalEqualProduct(row.cost_total,row.qty,row.unit_cost)||!decimalEqualProduct(row.purchase_cost_total,row.qty,authority.purchaseDecimal as string))
      throw new Error(`VIEWER_SMETA_ROWS_INVALID: priced row ${row.id} fails the money contract`);
  }
  if(!row.meta||typeof row.meta!=='object'||Array.isArray(row.meta))throw new Error('VIEWER_SMETA_ROWS_INVALID');
  const meta=row.meta as Record<string,unknown>;
  // Exact union emitted by ViewerSmetaRepository.rows(): manual rows use the
  // compact provenance keys, canonical-import rows use the projection keys.
  // sourceEvidence itself is a backend-versioned opaque governed JSON value.
  const metaKeys=['article','materialRowSha256','sourceId','sourceReleaseSha','schema','sourceKind','sourceEvidence',
    'sourceEntityId','sourceRowId','rowId','catalogId','catalogRevisionId','sourceUnit','authorityCardUnit','uomMatch',
    'cutRequired','bomSha256','cutSha256','sourceRevision','sourceArtifactSha256','physicalProjectionRevision'];
  if(Object.keys(meta).some((key)=>!metaKeys.includes(key))||!opaqueGovernedJson(meta)||
      ['article','materialRowSha256','sourceId','sourceReleaseSha','schema','sourceKind','sourceEntityId','sourceRowId','rowId',
        'sourceUnit','bomSha256','cutSha256','sourceRevision','sourceArtifactSha256','physicalProjectionRevision'].some((key)=>
        meta[key]!==undefined&&(typeof meta[key]!=='string'||!meta[key]))||
      ['catalogId','catalogRevisionId','authorityCardUnit'].some((key)=>meta[key]!==undefined&&meta[key]!==null&&
        (typeof meta[key]!=='string'||!meta[key]))||
      ['uomMatch','cutRequired'].some((key)=>meta[key]!==undefined&&typeof meta[key]!=='boolean'))
    throw new Error('VIEWER_SMETA_ROWS_INVALID');
  return row as unknown as ViewerSmetaCoreRow;
}

function exactRowSet(value:unknown,document:CoreDocument):{rows:ViewerSmetaCoreRow[];row_version:number;
    total_cost:string|null;purchase_total_cost:string|null;price_status:'COMPLETE'|'NOT_FINAL';unresolved_row_ids:string[];demand_refusals:string[];
    material_release_sha?:string;versionPins:NonNullable<CoreDocument['versionPins']>} {
  if(!value||typeof value!=='object'||Array.isArray(value))throw new Error('VIEWER_SMETA_ROWS_INVALID');
  const result=value as Record<string,unknown>;
  const pinned=document.versionPins?.estimateVersion!==undefined;
  const expected=pinned?['rows','row_version','published_smeta_row_version','total_cost','purchase_total_cost','price_status','unresolved_row_ids','demand_refusals','material_release_sha','sourceRevision','bomSha256','cutSha256','versionPins']:
    ['rows','row_version','published_smeta_row_version','total_cost','purchase_total_cost','price_status','unresolved_row_ids','demand_refusals','versionPins'];
  if(pinned&&('priceOverrideCount' in result||'priceOverrideSetSha256' in result)){
    expected.push('priceOverrideCount','priceOverrideSetSha256');
    if(!Number.isInteger(result.priceOverrideCount)||Number(result.priceOverrideCount)<0||
      (result.priceOverrideCount===0?result.priceOverrideSetSha256!==null:
        typeof result.priceOverrideSetSha256!=='string'||!HEX64.test(result.priceOverrideSetSha256)))
      throw new Error('VIEWER_SMETA_OVERRIDE_INVALID');
  }
  if(Object.keys(result).length!==expected.length||Object.keys(result).some((key)=>!expected.includes(key))||!Array.isArray(result.rows)||
      !Number.isInteger(result.row_version)||Number(result.row_version)<1||
      (result.published_smeta_row_version!==undefined&&result.published_smeta_row_version!==result.row_version)||
      (result.total_cost!==null&&(typeof result.total_cost!=='string'||!DECIMAL.test(result.total_cost)))||
      (result.material_release_sha!==undefined&&(typeof result.material_release_sha!=='string'||
        !HEX64.test(result.material_release_sha)))||
      (pinned&&['sourceRevision','bomSha256','cutSha256'].some((key)=>typeof result[key]!=='string'||!HEX64.test(result[key] as string))))
    throw new Error('VIEWER_SMETA_ROWS_INVALID');
  const receivedPins=exactPins(result.versionPins);
  if(JSON.stringify(receivedPins)!==JSON.stringify(exactPins(document.versionPins)))throw new Error('VIEWER_SMETA_ESTIMATE_PIN_MISMATCH');
  const rows=result.rows.map((row)=>exactCoreRow(row,document));
  if('priceOverrideCount' in result&&rows.filter(row=>row.priceAuthority?.override).length!==result.priceOverrideCount)
    throw new Error('VIEWER_SMETA_OVERRIDE_INVALID');
  if(pinned&&rows.some((row)=>row.meta.sourceRevision!==result.sourceRevision||row.meta.bomSha256!==result.bomSha256||
      row.meta.cutSha256!==result.cutSha256))throw new Error('VIEWER_SMETA_ROWS_INVALID');
  exactPriceReadiness(result,rows);
  return {rows,row_version:Number(result.row_version),total_cost:result.total_cost as string|null,purchase_total_cost:result.purchase_total_cost as string|null,
    price_status:result.price_status as 'COMPLETE'|'NOT_FINAL',unresolved_row_ids:result.unresolved_row_ids as string[],
    demand_refusals:result.demand_refusals as string[],
    ...(result.material_release_sha===undefined?{}:{material_release_sha:String(result.material_release_sha)}),
    versionPins:(result.versionPins===undefined?document.versionPins:exactPins(result.versionPins))!};
}

async function loadResolvedDocument(document: CoreDocument): Promise<{ smeta: Smeta; document: CoreDocument; rows: ViewerSmetaCoreRow[] }> {
  const expectedPins=document.versionPins;
  const estimateVersion=expectedPins?.estimateVersion;
  const pinned=estimateVersion !== undefined;
  if (pinned && (!Number.isInteger(estimateVersion) || estimateVersion < 1))
    throw new Error('VIEWER_SMETA_ESTIMATE_PIN_INVALID');
  const rawRowSet = await json<unknown>(
    pinned
      ? `/api/arm/smeta/${encodeURIComponent(document.smeta_id)}/estimates/${estimateVersion}/rows`
      : `/api/arm/smeta/${encodeURIComponent(document.smeta_id)}/rows`);
  const rowSet=exactRowSet(rawRowSet,document);
  const receivedPins=rowSet.versionPins;
  if (pinned && (!receivedPins || rowSet.row_version !== estimateVersion ||
      rowSet.material_release_sha !== document.material_release_sha ||
      receivedPins.estimateVersion !== estimateVersion ||
      receivedPins.estimateContentSha256 !== expectedPins?.estimateContentSha256 ||
      receivedPins.pricebookId !== expectedPins?.pricebookId ||
      receivedPins.pricebookSha256 !== expectedPins?.pricebookSha256 ||
      receivedPins.materialsVersionId !== expectedPins?.materialsVersionId ||
      receivedPins.ratesVersionId !== expectedPins?.ratesVersionId))
    throw new Error('VIEWER_SMETA_ESTIMATE_PIN_MISMATCH');
  const reloaded={...document,row_version:rowSet.row_version,total_cost:rowSet.total_cost,purchase_total_cost:rowSet.purchase_total_cost,
    price_status:rowSet.price_status,unresolved_row_ids:rowSet.unresolved_row_ids,demand_refusals:rowSet.demand_refusals,
    versionPins:rowSet.versionPins};
  return { document:reloaded, rows:rowSet.rows,
    smeta:coreToSmeta(reloaded,rowSet.rows,rowSet.row_version,rowSet.total_cost) };
}

async function loadNativeEstimateRows(smetaId:string,version:number):Promise<NativeEstimateRows> {
  if(!smetaId||!Number.isSafeInteger(version)||version<1)throw new Error('VIEWER_SMETA_ESTIMATE_PIN_INVALID');
  const [doc,raw]=await Promise.all([
    json<unknown>(`/api/arm/smeta/resolve?smeta_id=${encodeURIComponent(smetaId)}`),
    json<unknown>(`/api/arm/smeta/${encodeURIComponent(smetaId)}/estimates/${version}/rows`),
  ]);
  const current=exactDocument(doc,{});
  if(current.smeta_id!==smetaId||!raw||typeof raw!=='object'||Array.isArray(raw))throw new Error('VIEWER_SMETA_ESTIMATE_PIN_MISMATCH');
  const receipt=raw as Record<string,unknown>;
  const pins=exactPins(receipt.versionPins);
  if(pins?.estimateVersion!==version||receipt.row_version!==version||typeof receipt.material_release_sha!=='string')
    throw new Error('VIEWER_SMETA_ESTIMATE_PIN_MISMATCH');
  const bound={...current,material_release_sha:receipt.material_release_sha,versionPins:pins};
  const rowset=exactRowSet(raw,bound);
  return {rows:rowset.rows,row_version:rowset.row_version,total_cost:rowset.total_cost,
    purchase_total_cost:rowset.purchase_total_cost,price_status:rowset.price_status,
    unresolved_row_ids:[...rowset.unresolved_row_ids],demand_refusals:[...rowset.demand_refusals],versionPins:rowset.versionPins};
}

function exactQuoteVersion(raw:unknown,smetaId?:string):NativeQuoteVersion {
  if(!raw||typeof raw!=='object'||Array.isArray(raw))throw new Error('VIEWER_SMETA_QUOTE_VERSION_INVALID');
  const value=raw as Record<string,unknown>;
  const required=['quoteVersion','estimateVersion','label','totalCost','purchaseTotalCost','price_status','unresolved_row_ids','contentSha256'];
  const expected='schema' in value?[...required,'schema','smetaId','clientBriefUrl']:[...required,'createdAt','rowsCount'];
  if(Object.keys(value).length!==expected.length||Object.keys(value).some(key=>!expected.includes(key))||
    !Number.isSafeInteger(value.quoteVersion)||Number(value.quoteVersion)<1||
    !Number.isSafeInteger(value.estimateVersion)||Number(value.estimateVersion)<1||
    typeof value.label!=='string'||!value.label.trim()||typeof value.contentSha256!=='string'||!HEX64.test(value.contentSha256)||
    ('schema' in value?(value.schema!=='VIEWER_SMETA_QUOTE_VERSION.v1'||value.smetaId!==smetaId||
      value.clientBriefUrl!==`/api/arm/smeta/${smetaId}/brief/client.html?quoteVersion=${value.quoteVersion}`):
      typeof value.createdAt!=='string'||!Number.isFinite(Date.parse(value.createdAt))||
      !Number.isSafeInteger(value.rowsCount)||Number(value.rowsCount)<0))throw new Error('VIEWER_SMETA_QUOTE_VERSION_INVALID');
  exactPriceReadiness({...value,total_cost:value.totalCost,purchase_total_cost:value.purchaseTotalCost});
  if('rowsCount' in value&&(value.price_status==='COMPLETE'&&value.rowsCount===0||
      (value.unresolved_row_ids as string[]).length>Number(value.rowsCount)))throw new Error('VIEWER_SMETA_PRICE_COVERAGE_INVALID');
  return value as unknown as NativeQuoteVersion;
}

async function loadNativeQuoteVersions(smetaId:string):Promise<{items:NativeQuoteVersion[]}> {
  const raw=await json<unknown>(`/api/arm/smeta/${encodeURIComponent(smetaId)}/quote-versions`);
  if(!raw||typeof raw!=='object'||Array.isArray(raw)||Object.keys(raw).join(',')!=='items'||
    !Array.isArray((raw as Record<string,unknown>).items))throw new Error('VIEWER_SMETA_QUOTE_VERSION_INVALID');
  const items=((raw as {items:unknown[]}).items).map(item=>exactQuoteVersion(item));
  if(new Set(items.map(item=>item.quoteVersion)).size!==items.length)throw new Error('VIEWER_SMETA_QUOTE_VERSION_INVALID');
  return {items};
}

async function saveNativeQuoteVersion(smetaId:string,estimateVersion:number,label:string):Promise<NativeQuoteVersion> {
  return exactQuoteVersion(await json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/quote-versions`,{
    method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({estimateVersion,label})}),smetaId);
}

export type ViewerMaterialCandidate = {
  operationalId: number; canonicalId: string; governedId: string; sourceId: string;
  name: string; article: string | null; uom: string; status: 'ACTIVE'; rowSha256: string;
  releaseSha256: string; matchReason: 'EXACT_ARTICLE' | 'ACTIVE_ALIAS' | 'USER_QUERY' | 'NORMALIZED_ARTICLE_ALIAS' | 'COLOR_SUFFIX_ALIAS';
  priceStatus: 'APPROVED' | 'UNRESOLVED'; priceAuthority: Record<string, unknown> | null;
};

export type ViewerMaterialCandidates = {
  schema: 'VIEWER_SMETA_MATERIAL_CANDIDATES.v1'; rowId: string; releaseSha256: string;
  query: string; article: string; candidates: ViewerMaterialCandidate[]; quarantined: number;
  total: number; offset: number; limit: number; nextOffset: number | null;
};

export async function searchViewerMaterialCandidates(smetaId: string, rowId: string,
    query: string, article: string, offset=0): Promise<ViewerMaterialCandidates> {
  const params = new URLSearchParams();
  if (query.trim()) params.set('q', query.trim());
  if (article.trim()) params.set('article', article.trim());
  params.set('limit', '25'); params.set('offset', String(offset));
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/rows/${encodeURIComponent(rowId)}/material-candidates?${params}`,
    { headers: { Accept: 'application/json' } });
}

export async function proposeViewerMaterialLink(smetaId: string, rowId: string,
    baseRowVersion: number, targetOperationalId: number): Promise<{ id: string; state: 'PENDING' }> {
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/rows/${encodeURIComponent(rowId)}/material-link-proposals`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ baseRowVersion, targetOperationalId }),
  });
}

export type ViewerMaterialSubstitutionCandidates = Omit<ViewerMaterialCandidates,'schema'|'article'> & {
  schema:'VIEWER_SMETA_MATERIAL_SUBSTITUTION_CANDIDATES.v1'; sourceOperationalId:number;
};
export type ViewerMaterialSubstitutionProposal = { id:string; state:'PENDING'; baseRowVersion:number;
  patch:Array<{op:'substitute';path:string;value:{sourceOperationalId:number;targetOperationalId:number;
    sourcePriceRowSha256:string;targetPriceRowSha256:string;sourceCostTotal:string;targetCostTotal:string;deltaCostTotal:string}}>; autoApply:false;
  preview:{currency:'RUB';qty:string;unit:string;
    source:{operationalId:number;name:string;unitCost:string;costTotal:string};
    target:{operationalId:number;name:string;unitCost:string;costTotal:string};deltaCostTotal:string} };

export async function searchViewerMaterialSubstitutions(smetaId:string,rowId:string,query:string,
    offset=0):Promise<ViewerMaterialSubstitutionCandidates> {
  const params=new URLSearchParams({q:query.trim(),limit:'25',offset:String(offset)});
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/rows/${encodeURIComponent(rowId)}/material-substitution-candidates?${params}`,
    {headers:{Accept:'application/json'}});
}

export async function proposeViewerMaterialSubstitution(smetaId:string,rowId:string,
    baseRowVersion:number,targetOperationalId:number):Promise<ViewerMaterialSubstitutionProposal> {
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/rows/${encodeURIComponent(rowId)}/material-substitution-proposals`,{
    method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({baseRowVersion,targetOperationalId}),
  });
}

export async function applyViewerMaterialSubstitution(smetaId:string,proposalId:string,rowVersion:number):Promise<{
    id:string;state:'APPLIED';applied:true;row_version:number}> {
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/material-substitution-proposals/${encodeURIComponent(proposalId)}/apply`,{
    method:'POST',headers:{'If-Match':`"rv:${rowVersion}"`},
  });
}

export type ViewerMaterialAiAdvisory = { id: string; state: 'PENDING'|'REJECTED'; source: 'AI_ADVISORY';
  baseRowVersion: number; autoApply: false; replayed: boolean; advisory: { schema: 'VIEWER_SMETA_AI_MATERIAL_ADVISORY.v1';
    rowId: string; inputSha256: string; candidatesSha256: string; outputSha256: string;
    provider: string; model: string; materialReleaseSha256: string; output: {
      schema: 'VIEWER_SMETA_AI_MATERIAL_OUTPUT.v1'; selection: null | { operationalId: number; canonicalId: string };
      confidence: string; rationale: string; evidence: string[] } } };

export async function requestViewerMaterialAiAdvisory(smetaId: string, rowId: string, baseRowVersion: number,
    query: string, article: string): Promise<ViewerMaterialAiAdvisory> {
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/rows/${encodeURIComponent(rowId)}/ai-material-advisories`, {
    method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({baseRowVersion,query,article}),
  });
}

export async function convertViewerMaterialAiAdvisory(smetaId: string, advisoryId: string,
    baseRowVersion: number): Promise<{id:string;state:'PENDING'}> {
  return json(`/api/arm/smeta/${encodeURIComponent(smetaId)}/ai-material-advisories/${encodeURIComponent(advisoryId)}/convert`, {
    method:'POST',headers:{'If-Match':`"rv:${baseRowVersion}"`},
  });
}

function category(row: ViewerSmetaCoreRow): SmetaCategory {
  if (row.row_type === 'operation') return 'operation';
  return 'material';
}

export function coreToSmeta(document: CoreDocument, rows: ViewerSmetaCoreRow[], rowVersion: number, total: string|null): Smeta {
  const validatedRows=rows.map(row=>exactCoreRow(row,document));
  exactPriceReadiness({...document,total_cost:total},validatedRows);
  const pins=document.versionPins;
  const hasEstimatePin=pins && (pins.estimateVersion !== undefined ||
    pins.estimateContentSha256 !== undefined || pins.pricebookSha256 !== undefined);
  if (hasEstimatePin && (!Number.isInteger(pins.estimateVersion) || Number(pins.estimateVersion)<1 ||
      !pins.pricebookId || !/^[0-9a-f]{64}$/.test(pins.estimateContentSha256 ?? '') ||
      !/^[0-9a-f]{64}$/.test(pins.pricebookSha256 ?? '')))
    throw new Error('VIEWER_SMETA_ESTIMATE_PIN_INVALID');
  return { smetaId: document.smeta_id, status: document.status, rowVersion,
    displayOrderId: `Viewer · ${document.model_id}`, orderId: document.order_id ?? document.model_id,
    orderTitle: document.label, client: 'Проект Viewer', type: 'Мебель', daysLeft: 0,
    exactCostTotal: { value: document.purchase_total_cost, currency: 'RUB' },
    exactClientTotal: { value: total, currency: 'RUB' },
    canonicalPriceStatus: document.price_status,
    unresolvedRowIds: [...document.unresolved_row_ids],
    estimatePin:hasEstimatePin ? { estimateVersion:pins.estimateVersion!,
      estimateContentSha256:pins.estimateContentSha256!,pricebookId:pins.pricebookId!,
      pricebookSha256:pins.pricebookSha256! } : undefined,
    items: [{ id: document.smeta_id, name: document.label, qty: 1, unit: 'компл.',
      positions: validatedRows.map((row) => {
        const qty = Number(row.qty); const approved = row.priceStatus === 'APPROVED';
        const purchaseDecimal = row.priceAuthority?.purchaseDecimal;
        const clientDecimal = row.priceAuthority?.clientDecimal;
        if (approved && (typeof purchaseDecimal !== 'string' || typeof clientDecimal !== 'string' ||
            !DECIMAL.test(purchaseDecimal) || Number(purchaseDecimal)<=0 ||
            !DECIMAL.test(clientDecimal) || Number(clientDecimal)<=0 || clientDecimal !== row.unit_cost))
          throw new Error('VIEWER_SMETA_PRICE_AUTHORITY_MONEY_INVALID');
        const purchase = approved ? Number(purchaseDecimal) : null;
        const client = approved ? Number(clientDecimal) : null;
        if (approved && (!Number.isFinite(purchase) || !Number.isFinite(client)))
          throw new Error('VIEWER_SMETA_PRICE_AUTHORITY_MONEY_INVALID');
        const article = row.meta?.article;
        return { id: row.id, category: category(row), name: row.name,
          materialId: row.materialCanonicalId ?? undefined, article,
          catalogMaterialId: row.materialOperationalId ?? undefined,
          sourceMaterialArticle: article,
          qtyBasis: qty, qtyRaskr: qty, unit: row.unit,
          prices:approved?{cost:purchase!,production:purchase!,client:client!}:{cost:null,production:null,client:null},
          ...(approved ? {
            exactMoney: {qty:row.qty,unitCost:purchaseDecimal!,lineCost:row.purchase_cost_total!,currency:'RUB' as const},
            exactClientMoney:{qty:row.qty,unitCost:row.unit_cost!,lineCost:row.cost_total!,currency:'RUB' as const} } : {}),
          includeInTotals: true, hasValidationIssue: !approved, canonicalPriceStatus: row.priceStatus,
          pricingSource: row.priceStatus === 'APPROVED' ? 'Viewer material authority' : 'unresolved',
          pricingIssue: row.priceStatus === 'APPROVED' ? undefined : 'Нет утверждённой цены',
          sourceLabel: `Viewer PG · ${row.materialReleaseSha ?? document.material_release_sha.slice(0, 12)}`,
          sourceKind:'basis' as const,dbOperationSku:undefined,operationPricingContract:undefined,
          ruleSource:undefined,isCompanion:false,addedBySystem:false };
      }) as unknown as Smeta['items'][number]['positions'] }],
  };
}

export async function viewerSmetaRest<T>(path: string, init?: { method?: string; headers?: Record<string, string>; body?: unknown; signal?: AbortSignal }): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  let body = init?.body;
  /* НЕ МЁРТВЫЙ КОД (статик-аудит 09.10 ложного «мёртвого» флага): спец-ветки
   * row-comments / row-comments/{id}/resolve / readiness — живой контракт с
   * vendor-пакетом @holzcom/smeta-kp (vendor/smeta-kp/src):
   *   • SmetaScreen.tsx:606 → getSmetaReadiness() → restFetch('…/readiness')
   *     (ветка ниже маппит бэкенд-проекцию в тип SmetaReadiness с blockers —
   *     без неё бейдж готовости падает на readiness.blockers.some);
   *   • SmetaCellComments.tsx → addRowComment()/resolveRowComment() шлют
   *     {audience, cell, author_id, …}: бэкенд CommentBody = extra="forbid"
   *     (viewer_smeta_core.py:81), поэтому reshape/strip ниже обязателен,
   *     иначе каждый комментарий — 422.
   * Вызывающие монтируются через SmetaTransportBoundary (SharedSmetaWorkspace
   * → configureSmetaTransport({rest: viewerSmetaRest})). */
  if (method === 'POST' && /\/row-comments$/.test(path) && body && typeof body === 'object') {
    const input = body as { row_id?: string; body?: string };
    body = { row_id: input.row_id, body: input.body };
  }
  if (method === 'POST' && /\/row-comments\/[^/]+\/resolve$/.test(path)) body = undefined;
  const request = { method,
    signal: method === 'GET' ? init?.signal : undefined,
    headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }), ...init?.headers },
    body: body === undefined ? undefined : JSON.stringify(body) };
  const decode=async(response:Response):Promise<Record<string,unknown>>=>{
    if (!response.ok) throw new Error(`VIEWER_SMETA_ROUTE_${response.status}`);
    return await response.json() as Record<string,unknown>;
  };
  const result=method==='GET'
    ? await criticalApiJson(path,request,decode)
    : await decode(await smetaFetch(path,request));
  if (method === 'GET' && /\/readiness(?:\?|$)/.test(path)) {
    const unresolved = Number(result.unresolved ?? 0);
    const status = String(result.status ?? 'DRAFT');
    return { status, frozen: result.frozen === true,
      ready: Boolean(result.ready), validated_fresh: Boolean(result.ready),
      rows_total: undefined, blockers: unresolved ? [{ code: 'PRICE_UNRESOLVED', severity: 'P0',
        title: 'Нет утверждённой цены', count: unresolved, rows: [], fix: null,
        hint: 'Строки остаются unresolved; локальный fallback запрещён.' }] : [], next_fix: null } as T;
  }
  return result as T;
}
